import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, RefreshCw, User2, PackageCheck, CircleAlert, Clock } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { displaySerial, isPlaceholderSerial } from "@/lib/serials";
import { QC } from "@/constants/testIds";

const STATUS_LABELS = {
  PENDING: { label: "Pending", color: "bg-amber-100 text-amber-800", icon: Clock },
  PASSED: { label: "Passed", color: "bg-emerald-100 text-emerald-800", icon: PackageCheck },
  FAILED: { label: "Failed", color: "bg-rose-100 text-rose-800", icon: CircleAlert },
};

function passportStatusChip(status) {
  const map = {
    QC_PENDING: STATUS_LABELS.PENDING,
    QC_PASSED: STATUS_LABELS.PASSED,
    AVAILABLE: STATUS_LABELS.PASSED,
    VENDOR_CASE: STATUS_LABELS.FAILED,
  };
  const m = map[status] || { label: status, color: "bg-slate-100 text-slate-700", icon: Clock };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${m.color}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

// V2.1 — Routing helper for the QC queue.
//   A passport whose Manufacturer Serial Number is still a placeholder
//   (PENDING-* / TMP-* / AWAITING-* / PLACEHOLDER-*) must go through the
//   Purchase QC Job screen FIRST, where the operator captures the real
//   serial before the per-passport checklist can start.
//   Once a real serial is on the passport, the operator jumps straight
//   into the per-passport checklist page as before — preserving the
//   existing workflow for already-serialised units.
function qcOpenUrl(row) {
  if (isPlaceholderSerial(row?.manufacturer_serial) && row?.purchase_row_id) {
    return `/admin/purchases/${row.purchase_row_id}/qc-job`;
  }
  return `/admin/qc/jobs/${row?.passport_id}`;
}

export default function QCJobsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("PENDING");
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [barcode, setBarcode] = useState("");
  const barcodeRef = useRef(null);

  const params = useMemo(() => ({
    page, page_size: pageSize,
    q: q || undefined,
    status: status || undefined,
    mine: mine || undefined,
  }), [q, status, mine, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/qc/jobs", { params });
      setRows(data.items);
      setMeta(data.meta);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load QC queue"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcode.trim()) return;
    try {
      const { data } = await api.get(`/qc/jobs/lookup`, { params: { code: barcode.trim() } });
      navigate(qcOpenUrl(data));
      setBarcode("");
    } catch (err) {
      setError(errorToMessage(err, "No passport matched that code"));
      barcodeRef.current?.focus();
    }
  };

  return (
    <div className="space-y-6" data-testid="qc-jobs-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">QC Job Queue</h1>
          <p className="text-sm text-slate-500 mt-1">
            Inspect refurbished laptops. Scan a barcode or search below to open a job.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Barcode scan bar */}
      <form
        onSubmit={handleBarcodeSubmit}
        className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-3 shadow-sm"
      >
        <ScanLine className="text-slate-400" size={20} />
        <input
          ref={barcodeRef}
          type="text"
          data-testid={QC.barcodeInput}
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          autoFocus
          placeholder="Scan or type barcode / serial and press Enter"
          className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
        />
        <button
          type="submit"
          data-testid={QC.barcodeGo}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Open Job
        </button>
      </form>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 shadow-sm">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid={QC.searchInput}
            placeholder="Barcode, serial, brand, model, purchase-id, processor"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            data-testid={QC.statusFilter}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          >
            <option value="">All</option>
            <option value="PENDING">Pending</option>
            <option value="PASSED">Passed</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
        <div className="flex items-end">
          <label
            data-testid={QC.mineToggle}
            className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => { setMine(e.target.checked); setPage(1); }}
              className="h-4 w-4 accent-slate-900"
            />
            Assigned to me
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={QC.queueTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Barcode / SN</th>
              <th className="text-left px-4 py-3">Model</th>
              <th className="text-left px-4 py-3">Config</th>
              <th className="text-left px-4 py-3">Purchase</th>
              <th className="text-left px-4 py-3">Vendor</th>
              <th className="text-left px-4 py-3">Engineer</th>
              <th className="text-left px-4 py-3">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No jobs match this filter.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr
                key={r.passport_id}
                data-testid={QC.queueRow(r.passport_id)}
                className="border-t border-slate-100 hover:bg-slate-50"
              >
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-slate-900">{displaySerial(r.internal_barcode)}</div>
                  <div className="font-mono text-xs text-slate-500">{displaySerial(r.manufacturer_serial)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{r.brand} {r.model}</div>
                  <div className="text-xs text-slate-500">{r.processor}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.ram_gb} GB · {r.storage_gb} GB {r.storage_type}
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-mono text-slate-700">{r.purchase_id}</div>
                  <div className="text-slate-500">{r.purchase_date}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{r.vendor_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700">
                  {r.assigned_engineer_name ? (
                    <span className="inline-flex items-center gap-1 text-xs">
                      <User2 size={12} className="text-slate-500" /> {r.assigned_engineer_name}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3">{passportStatusChip(r.current_status)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => navigate(qcOpenUrl(r))}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{meta.total} job{meta.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button
              data-testid={QC.pagePrev}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40"
            >Prev</button>
            <span>Page {page} of {meta.total_pages || 1}</span>
            <button
              data-testid={QC.pageNext}
              disabled={page >= (meta.total_pages || 1)}
              onClick={() => setPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
